import { ReactNode } from 'react';
import { UtMatrixOption } from '../types';
import CompareTargetTable, { CompareTargetActionColumn } from './CompareTargetTable';
import SectionBox from './SectionBox';

interface Props {
  /** 배열 순서가 곧 구분 — 0=기준, 1=비교대상, 2=참조 */
  matrices: UtMatrixOption[];
  /**
   * 저장된 리포트를 연 상태면 그 rptId.
   * 이때는 대상이 비어 있어도 "없음"이 아니라 "불러오는 중"이다 — 곧 채워지기 때문.
   */
  historyRptId?: string;
  /** historyRptId가 없을 때 빈 표에 넣을 안내 문구 */
  emptyText?: ReactNode;
  /** 빈 행 셀 클래스 — 기계는 `empty-state`, 전기는 `empty`로 스타일이 따로 있다 */
  emptyClassName?: string;
  /** 표를 감싸는 상자 클래스 — 기계는 `table-outer`, 전기는 `pane-body` */
  bodyClassName: string;
  /** 제목줄 우측 액션 (전기 탭의 행추가/초기화/분석시작) */
  actions?: ReactNode;
  /** 표 우측 조작 열 (전기 탭의 선택 해제 ✕) */
  trailing?: CompareTargetActionColumn;
  /** 본문 끝에 덧붙일 것 (전기 탭의 UT Matrix 선택 모달) */
  children?: ReactNode;
}

/**
 * ① 비교 대상 UT Matrix 영역 — 기계/전기 탭 공용.
 *
 * 표(CompareTargetTable)는 이미 공용이었고, 그 바깥의 섹션 머리·건수·빈 표 문구까지
 * 두 탭이 같은 모양이라 여기로 합쳤다. 정합화 Report 상세에서는 두 탭이 같은
 * `getReportDetail(rptId).targets`를 읽으므로 내용도 동일하다.
 *
 * ⚠ 데이터는 props로 받는다 — 기계는 useUtComparisonStore, 전기는 useUtComparisonElecStore로
 *   스토어가 서로 다르기 때문이다. 탭별로 다른 것(액션 버튼, 조작 열, 안내 문구, 상자 클래스)만
 *   슬롯으로 열어 두고 나머지는 여기서 고정한다.
 */
const CompareTargetSection = ({
  matrices,
  historyRptId,
  emptyText = '표시할 비교 대상 UT Matrix가 없습니다.',
  emptyClassName,
  bodyClassName,
  actions,
  trailing,
  children,
}: Props) => (
  <SectionBox no="①" title="비교 대상 UT Matrix" count={matrices.length} actions={actions}>
    <div className={bodyClassName}>
      <CompareTargetTable
        matrices={matrices}
        emptyClassName={emptyClassName}
        empty={historyRptId ? '저장된 리포트를 불러오는 중입니다…' : emptyText}
        trailing={trailing}
      />
    </div>
    {children}
  </SectionBox>
);

export default CompareTargetSection;
